import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import { getList, setListQuantity, removeFromList, clearList } from "../../services/portalService";
import { errorToast } from "../Toasts/Toasts";

// The society's working list: what they have noticed they need, gathered
// over however many days it takes, and turned into one request when they
// are ready. Not a cart - nothing is reserved and nothing is ordered here.

const inr = (n) => (n === null || n === undefined ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);

const RequirementListPage = () => {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try { setList(await getList()); } catch (e) { errorToast("Could not load your list"); setList({ items: [], total: 0, changed: [] }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const change = async (id, quantity) => {
    setBusy(true);
    try { setList(await setListQuantity(id, quantity)); } catch (e) { errorToast(e?.message); } finally { setBusy(false); }
  };
  const drop = async (id) => {
    setBusy(true);
    try { setList(await removeFromList(id)); } catch (e) { errorToast(e?.message); } finally { setBusy(false); }
  };
  const empty = async () => {
    if (!window.confirm("Clear the whole list?")) return;
    setBusy(true);
    try { setList(await clearList()); } catch (e) { errorToast(e?.message); } finally { setBusy(false); }
  };

  const send = () => {
    const text = (list.items || [])
      .map((i) => `${i.name}${i.size ? ` ${i.size}` : ""} - ${i.quantity} nos${i.note ? ` (${i.note})` : ""}`)
      .join("\n");
    navigate("/portal/requirements/new", { state: { text, fromList: true } });
  };

  if (!list) return null;

  if (!list.items.length) {
    return (
      <div className="portal-page">
        <h5 className="fw-bold">Your list</h5>
        <div className="portal-empty">
          Nothing on your list yet. Add what you need from <Link to="/portal/plants">Plants</Link>, or{" "}
          <Link to="/portal/requirements/new">write it out</Link> if it is easier.
        </div>
      </div>
    );
  }

  return (
    <div className="portal-page">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h5 className="fw-bold mb-0">Your list</h5>
          <div className="small text-muted">Add to it whenever you notice something. Send it when you are ready — we will confirm availability and quote.</div>
        </div>
        <Button size="sm" variant="link" className="text-danger p-0" onClick={empty} disabled={busy}>Clear</Button>
      </div>

      {list.changed.length > 0 && (
        <div className="alert alert-warning py-2 small">
          {list.changed.map((c) => <div key={c.id}>{c.name} — {c.reason}.</div>)}
          <div className="mt-1">You can still send these; we will tell you what we can do.</div>
        </div>
      )}

      <div className="portal-card p-0">
        {list.items.map((i) => (
          <div key={i.id} className={`d-flex align-items-center gap-3 p-2 border-bottom ${i.available ? "" : "bg-light"}`}>
            {i.image
              ? <img src={i.image} alt={i.name} style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6 }} />
              : <div style={{ width: 56, height: 56, borderRadius: 6, background: "#eef3ef" }} />}
            <div className="flex-grow-1">
              <div className="fw-semibold" style={{ fontSize: 15 }}>{i.name}</div>
              <div className="small text-muted">
                {i.size}
                {i.price !== null ? ` · ${inr(i.price)} each` : " · price on the quotation"}
                {!i.available && <span className="text-warning"> · not available right now</span>}
              </div>
            </div>
            <Form.Control
              size="sm"
              type="number"
              min="0"
              inputMode="numeric"
              style={{ width: 76 }}
              value={i.quantity}
              disabled={busy}
              onChange={(e) => change(i.id, e.target.value)}
            />
            <Button size="sm" variant="link" className="text-muted p-0" onClick={() => drop(i.id)} disabled={busy}>remove</Button>
          </div>
        ))}
      </div>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          <div className="fw-bold">{inr(list.total)}</div>
          <div className="small text-muted">Indicative, before GST and delivery to your site</div>
        </div>
        <Button variant="success" onClick={send} disabled={busy}>Check availability &amp; get a quote</Button>
      </div>
    </div>
  );
};

export default RequirementListPage;
